
import { useEffect, useState } from 'react';
import { getImpactStories } from '../../../api/apiServices';

export default function ImpactStories() {
  const [selectedStory, setSelectedStory] = useState(null);
  const [activeCategory, setActiveCategory] = useState('All');
  const [stories, setStories] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
      fetchStories();
    }, []);
  
    const fetchStories = async () => {
      try {
        setLoading(true);
        const res = await getImpactStories();
        setStories(res.data || []);
      } catch (err) {
        console.error("Failed to load stories", err);
      } finally {
        setLoading(false);
      }
    };

    /* ---------------- HELPERS ---------------- */

  const getThumbnail = (story) => {
    const img = story?.content?.find(c => c.type === "image");
    return img?.image || null;
  };

  const getExcerpt = (story) => {
    if (story.excerpt) return story.excerpt;

    const paragraph = story?.content?.find(c => c.type === "paragraph");
    return paragraph?.text?.slice(0, 140) || "No preview available";
  };

  const categories = [
    'All',
    ...Array.from(
      new Set(stories.map((story) => story.category).filter(Boolean))
    ),
  ];

  const filteredStories =
    activeCategory === 'All'
      ? stories
      : stories.filter(story => story.category === activeCategory);

  return (
    <section id="stories" className="py-16 sm:py-24 px-4 sm:px-6 lg:px-20 bg-white">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="grid lg:grid-cols-5 gap-6 sm:gap-12 mb-10 sm:mb-16">
          <div className="lg:col-span-2">
            <p className="text-gray-500 text-sm mb-3">(Real Stories of Hope)</p>
            <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold leading-tight">
              <span className="text-gray-900">Voices of</span>
              <br />
              <span className="text-gray-400">Resilience</span>
            </h2>
          </div>
          <div className="lg:col-span-3">
            <p className="text-gray-700 leading-relaxed text-sm sm:text-base">
              Every survivor has a unique story of courage and resilience. These stories represent the transformative power of compassionate support and the incredible strength of the human spirit. Through our comprehensive programs, survivors find not just recovery, but renewed purpose and hope for the future.
            </p>
          </div>
        </div>

        {/* Stories Grid */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
          {filteredStories.map((story) => {
            const thumbnail = getThumbnail(story);

            return (
              <div
                key={story._id}
                className="bg-white rounded-2xl shadow-lg hover:shadow-xl transition hover:-translate-y-1 overflow-hidden cursor-pointer"
                onClick={() => setSelectedStory(story)}
              >
                {/* Image */}
                <div className="w-full h-60 bg-gray-100 overflow-hidden">
                  {thumbnail ? (
                    <img
                      src={thumbnail}
                      alt={story.title}
                      className="w-full h-full object-cover hover:scale-105 transition"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-gray-400 text-sm">
                      No Image
                    </div>
                  )}
                </div>

                {/* Content */}
                <div className="p-6">
                  <span className="inline-block bg-[#c9a24d]/10 text-[#c9a24d] px-3 py-1 rounded-full text-xs font-semibold mb-3">
                    {story.category}
                  </span>

                  <h3 className="text-lg font-bold text-gray-900 mb-2 line-clamp-2">
                    {story.title}
                  </h3>

                  <p className="text-gray-600 text-sm mb-4 line-clamp-3">
                    {getExcerpt(story)}
                  </p>

                  <div className="text-[#6a4bc3] font-semibold text-sm flex items-center gap-2">
                    Read Full Story
                    <i className="ri-arrow-right-line"></i>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Story Modal */}
      {selectedStory && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4"
          onClick={() => setSelectedStory(null)}>

          <div
            className="bg-white rounded-3xl max-w-3xl w-full max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header Image */}
            {getThumbnail(selectedStory) && (
              <div className="relative w-full h-72">
                <img
                  src={getThumbnail(selectedStory)}
                  alt=""
                  className="w-full h-full object-cover"
                />
                <button
                  onClick={() => setSelectedStory(null)}
                  className="absolute top-6 right-6 w-12 h-12 bg-white rounded-full flex items-center justify-center shadow-lg"
                >
                  <i className="ri-close-line text-xl"></i>
                </button>
              </div>
            )}

            {/* Article Body */}
            <div className="p-8">
              <span className="inline-block bg-[#c9a24d]/10 text-[#c9a24d] px-3 py-1 rounded-full text-xs font-semibold mb-4">
                {selectedStory.category}
              </span>

              <h2 className="text-3xl font-bold text-gray-900 mb-6">
                {selectedStory.title}
              </h2>

              {/* RICH CONTENT RENDER */}
              <div className="space-y-6 leading-relaxed text-gray-700">

                {selectedStory.content?.map((block, index) => {

                  if (block.type === "paragraph") {
                    return (
                      <p key={index} className="text-base leading-8">
                        {block.text}
                      </p>
                    );
                  }

                  if (block.type === "image") {
                    return (
                      <img
                        key={index}
                        src={block.image}
                        alt=""
                        className="w-full rounded-xl shadow-md"
                      />
                    );
                  }

                  return null;
                })}

              </div>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
